import React, { useCallback, useState } from 'react';
import 'twin.macro';
import Input from '@teetsh/app/src/components/Form/InputGroup';
import { SwitchToggle } from '@teetsh/app/src/components/Form/SwitchToggle';
import { TrashIcon } from '@heroicons/react/24/outline';
import _debounce from 'lodash-es/debounce';
import DeleteButton from '@teetsh/app/src/components/Buttons/DeleteButton';
import Select from '@teetsh/app/src/components/Form/Select2';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import { v4 as uuid } from 'uuid';
import {
  CustomFieldDefinition,
  CustomFieldDefinitionDefaults,
  CustomFieldTarget,
  FieldType,
} from '@teetsh/app/src/types/customField';
import { useTranslation } from 'react-i18next';
import { ParseKeys } from 'i18next';
import H5 from 'components/Typography/Headings/H5';
import ListInputs from '../components/ListInputs';
import ListInputsWithColors from '../components/ListInputsWithColors';
import ButtonGroup from 'components/Form/ButtonGroup';
import Button from 'components/Buttons/Button';
import tw, { styled } from 'twin.macro';

type I18nTargetKey = Extract<
  ParseKeys<'userSettings', {}, undefined>,
  `customFields.customField.${string}`
>;

const targetLabelMapping: { [K in CustomFieldTarget]: I18nTargetKey } = {
  cahierJournal: 'customFields.customField.displayInCahierJournal',
  headCahierJournal: 'customFields.customField.displayInCahierJournalAtTheTop',
  fdp: 'customFields.customField.displayInFichesDePrep',
  fdpPhases: 'customFields.customField.displayInFichesDePrepPhases',
  fdpSummary: 'customFields.customField.displayInFichesDePrepSummary',
  sequence: 'customFields.customField.displayInSequences',
};

interface Props {
  item: CustomFieldDefinition;
  onChange: (item: CustomFieldDefinition) => void;
  onDelete: (item: CustomFieldDefinition) => void;
  onReorder?: (diff: -1 | 1) => void;
  isFirstItem?: boolean;
  isLastItem?: boolean;
  availableTypes: FieldType[];
  inline?: boolean;
  definitionTargets: CustomFieldTarget[];
  availableTargets?: CustomFieldTarget[];
  onTargetToggle?: (target: CustomFieldTarget) => void;
}

export function CustomFieldDefinitionComponent(props: Props) {
  const { availableTypes, onReorder, isFirstItem, isLastItem } = props;
  const [item, setItem] = useState<CustomFieldDefinition>(props.item);
  const { t } = useTranslation(['userSettings', 'common']);

  const debouncedSave = useCallback(
    _debounce(nextValue => props.onChange(nextValue), 500),
    [],
  );
  const handleChange = (nextValue: CustomFieldDefinition) => {
    setItem(nextValue);
    debouncedSave(nextValue);
  };
  const hasPlural = CustomFieldDefinitionDefaults[item.type].hasPluralName;
  const StyledIcon = styled.div`
    ${tw`h-5 w-5 mr-2`}
  `;

  return (
    <>
      <div tw="mb-4">
        <Select
          options={Object.values(CustomFieldDefinitionDefaults)
            .filter(opt => (availableTypes || []).includes(opt.value))
            .map(option => ({
              value: option.value,
              label: (
                <div tw="flex items-center">
                  <StyledIcon as={option.icon as any} />
                  {t(`common:customFields.types.${option.value}`)}
                </div>
              ),
            }))}
          onChange={type => handleChange({ ...item, type })}
          label={t('userSettings:customFields.customField.type')}
          value={item.type}
        />
      </div>
      <div tw="mb-5">
        <Input
          id={`customfield-singular-${item.id}`}
          value={item.name}
          label={
            hasPlural
              ? t('userSettings:customFields.customField.nameSingular')
              : t('userSettings:customFields.customField.name')
          }
          onChange={name => {
            const pluralName =
              !item.pluralName || item.pluralName === item.name
                ? name
                : item.pluralName;
            return handleChange({ ...item, pluralName, name });
          }}
        />
        {hasPlural && (
          <Input
            id={`customfield-pluralName-${item.id}`}
            value={item.pluralName || ''}
            label={t('userSettings:customFields.customField.namePlural')}
            onChange={pluralName => handleChange({ ...item, pluralName })}
          />
        )}
      </div>
      {(props.availableTargets || [])?.length > 0 && (
        <div tw="mb-5">
          {props.availableTargets?.map(target => (
            <SwitchToggle
              key={target}
              tw="mb-1"
              label={t(targetLabelMapping[target])}
              size="sm"
              onChange={() => props.onTargetToggle?.(target)}
              checked={props.definitionTargets?.includes(target)}
            />
          ))}
        </div>
      )}

      {item.type === 'multiselect' && (
        <>
          <H5 tw="mb-2">
            {t('userSettings:customFields.customField.multiselectList')}
          </H5>
          <ListInputs
            inline={props.inline}
            items={item.options.map(v => v.value)}
            onChange={newItems =>
              handleChange({
                ...item,
                options: newItems.map(v => ({ id: uuid(), value: v })),
              })
            }
          />
        </>
      )}
      {item.type === 'select' && (
        <>
          <H5 tw="mb-2">
            {t('userSettings:customFields.customField.multitagList')}
          </H5>
          <div>
            <ListInputsWithColors
              inline={props.inline}
              items={item.options}
              onChange={newItems =>
                handleChange({ ...item, options: newItems })
              }
            />
          </div>
        </>
      )}

      <div tw="absolute top-2 right-2 flex">
        {onReorder && (
          <ButtonGroup minimal>
            {!isFirstItem && (
              <Button
                onClick={() => onReorder(-1)}
                minimal
                icon={ArrowUpIcon}
              />
            )}
            {!isLastItem && (
              <Button
                onClick={() => onReorder(1)}
                minimal
                icon={ArrowDownIcon}
              />
            )}
          </ButtonGroup>
        )}
        <DeleteButton
          withConfirmation
          aria-label="Supprimer le champ"
          minimal
          icon={TrashIcon}
          popoverText={
            <div>
              <div tw="mb-2 font-bold">
                {t(
                  'userSettings:customFields.customField.confirmDeleteCustomFieldTitle',
                )}
              </div>
              <div tw="text-sm">
                {t(
                  'userSettings:customFields.customField.confirmDeleteCustomFieldDetails',
                )}
              </div>
            </div>
          }
          onClick={() => props.onDelete(item)}
        />
      </div>
    </>
  );
}
